// Central, single-sourced friendly copy for HTTP failures that arrive WITHOUT a
// server-provided `error` sentence — a proxy/gateway 5xx HTML page, an empty
// body, or a JSON error missing its `error` field. This replaces the old
// `Request failed (500)` status-shaped string in api.ts, which was the one place
// a bare status code reached the user.
//
// The server already sends a human, operational sentence for every intentional
// refusal (api.ts prefers that sentence and renders it as-is), so this map is
// only the last-resort fallback for the error-less cases.
//
// COMPLIANCE: every string is factual/operational — how to recover or what
// happened — with no buy/sell/should, no predictions, no rankings (the advice
// line). These are plain English and double as i18n lookup keys: a render site
// that wraps the message in t() localises it when a catalog entry exists and
// falls back to English otherwise.
export function statusToMessage(status: number): string {
  if (status >= 500) return 'The server had a problem handling that. Please try again in a moment.';
  switch (status) {
    case 400:
      return 'Something in that request wasn’t right. Check your input and try again.';
    case 401:
      return 'Your session has ended. Please sign in again.';
    // Conv-37: a lapsed subscription. Every /api route outside the documented
    // exempt set answers 402 SUBSCRIPTION_EXPIRED, and it normally carries its own
    // sentence — this is the last-resort fallback, and it names the next step
    // (choose a plan) rather than saying something went wrong.
    case 402:
      return 'Your Sansara plan has ended. Choose a plan to get back in.';
    case 403:
      return 'You don’t have access to that.';
    case 404:
      return 'We couldn’t find what you were looking for.';
    case 408:
      return 'That took too long. Check your connection and try again.';
    case 409:
      return 'That doesn’t match the latest state. Refresh and try again.';
    case 413:
      return 'That upload is too large.';
    case 422:
      return 'We couldn’t process that. Check your input and try again.';
    case 429:
      return 'Too many requests just now. Please wait a moment and try again.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
